/**
 * Knowledge Cloud — prompt template / instance / sharing endpoints, plus
 * admin prompt-instance detail.
 */

import { ApiError, extractError, get, post } from '../_shared.ts';

export interface AdminPromptInstanceDetail {
  id: string;
  /** Phase 2 Block 2A — knowledge__prompt.short_id (kpi-prefix). */
  short_id: string;
  template_id: string | null;
  template_slug: string | null;
  name: string;
  display_name: string;
  description: string | null;
  // Push #4.6 / Migration 0117 — per-instance usage guidance for the AI client.
  usage_guidance: string | null;
  body: string;
  category: string | null;
  visibility: string;
  created_by_user_id: string | null;
  /** Push 4-1aj (2026-07-10): count of DISTINCT account_users with active
   *  access to this prompt via direct or PS-mediated assignment. Powers the
   *  Delete ConfirmDialog impact bullet. Wire-shape is string (COUNT bigint). */
  assigned_user_count: string;
  created_at: string;
}
/**
 * Phase 2 Block 2A Cluster B (2026-06-30) — URL flattened from
 *   /api/admin/knowledge/instances/:id  →  /api/admin/knowledge/:short_id
 * Caller passes knowledge__prompt.short_id (kpi-prefix).
 */
export function fetchAdminPromptInstance(short_id: string): Promise<AdminPromptInstanceDetail> {
  return get(`/api/admin/knowledge/${encodeURIComponent(short_id)}`);
}

export interface AdminPromptInstancePatch {
  display_name?: string;
  description?: string | null;
  usage_guidance?: string | null;
  body?: string;
  category?: string | null;
  visibility?: 'account_installed' | 'account_custom' | 'user_private' | 'user_shared';
}
/** Phase 2 Block 2A — URL flattened; param is knowledge__prompt.short_id. */
export function updateAdminPromptInstance(
  short_id: string,
  patch: AdminPromptInstancePatch,
): Promise<{ ok: true; changed: number }> {
  return fetch(`/api/admin/knowledge/${encodeURIComponent(short_id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(async (res) => {
    const text = await res.text();
    const parsed: unknown = text === '' ? null : (JSON.parse(text) as unknown);
    if (!res.ok) {
      const msg =
        parsed !== null && typeof parsed === 'object' && 'message' in parsed
          ? String((parsed as { message: unknown }).message)
          : `PATCH /api/admin/knowledge/${short_id} failed (${res.status})`;
      throw new ApiError(res.status, msg);
    }
    return parsed as { ok: true; changed: number };
  });
}

/**
 * Push 4-1l (2026-07-09) — power-admin per-account override for a prompt.
 * Prompts are user-authored (no template layer post Migration 0143), so
 * this endpoint is a redundant PATCH surface pinned to the standalone
 * "Override defaults" section on AdminPromptDetail. Passes an optional
 * subset of columns on knowledge__prompt.
 */
export interface PromptOverrideBody {
  display_name?: string;
  description?: string | null;
  body?: string;
  category?: string | null;
  visibility?: 'account_installed' | 'account_custom' | 'user_private' | 'user_shared';
  usage_guidance?: string | null;
  variables?: unknown;
}
export function overrideAdminPromptInstance(
  short_id: string,
  body: PromptOverrideBody,
): Promise<{ ok: true; prompt_id: string }> {
  return post(
    `/api/admin/knowledge/${encodeURIComponent(short_id)}/override`,
    body,
  );
}

/**
 * Push 4-1g (2026-07-08): Clone a prompt instance. Deep-copies the source
 * row under a new short_id + display_name; fresh clones start unassigned.
 * Endpoint returns 501 until the clone machinery lands — SPA surfaces the
 * humanized 501 message via humanizeApiError.
 */
export async function cloneAdminPromptInstance(
  short_id: string,
): Promise<{ ok: true; short_id: string }> {
  const path = `/api/admin/knowledge/${encodeURIComponent(short_id)}/clone`;
  const res = await fetch(path, { method: 'POST', credentials: 'include' });
  const text = await res.text();
  if (!res.ok) {
    const errInfo = extractError(text, path, 'POST', res.status);
    throw new ApiError(res.status, errInfo.message, errInfo.detail, errInfo.incidentId, errInfo.code);
  }
  return JSON.parse(text) as { ok: true; short_id: string };
}

/**
 * Push 4-1g (2026-07-08): Delete a prompt instance. Soft-deletes the row +
 * cascades assignment junctions. Prompts are user-authored (Group 2 BYO)
 * so "delete" is the correct verb.
 */
export async function deleteAdminPromptInstance(short_id: string): Promise<{ ok: true }> {
  const path = `/api/admin/knowledge/${encodeURIComponent(short_id)}/delete`;
  const res = await fetch(path, { method: 'POST', credentials: 'include' });
  const text = await res.text();
  if (!res.ok) {
    const errInfo = extractError(text, path, 'POST', res.status);
    throw new ApiError(res.status, errInfo.message, errInfo.detail, errInfo.incidentId, errInfo.code);
  }
  return JSON.parse(text) as { ok: true };
}

// ---- Knowledge Cloud ----
//
// Interfaces aligned to migration 0029 (explicit WITH CHECK on p_kpi_update)
// + Migration 0144 (Push 4-1c-b, 2026-07-01) which dropped
// workspace_directory.knowledge__prompt_template. Prompts are Group 2 BYO
// now — customers author + own them in workspace_selfservice.knowledge__prompt
// directly. Per CLAUDE.md "TS interfaces match DB columns" discipline, the
// DB is source of truth — these mirror knowledge__prompt exactly.
//
// KnowledgePromptTemplate type retained for callers (fetchKnowledgeCatalog +
// fetchMyKnowledgeCatalog return empty stub arrays post-0144). Delete the
// type entirely when those two functions get removed in a future cleanup.

export interface KnowledgePromptTemplate {
  id: string;
  /** DB column `name` — slug-style identifier (lowercase, hyphen-separated). */
  name: string;
  display_name: string;
  description: string | null;
  /** Prompt body with {{variable}} substitution slots. */
  body: string;
  variables: Array<{ name: string; description?: string; required?: boolean }>;
  category: string | null;
  /** Computed from `status = 'coming_soon'` (per D-156 migration 0042). UI labels as "coming soon". */
  is_preview: boolean;
  /** Convenience alias of `name` for AdminKnowledge.tsx existing matcher use. */
  slug: string;
}

export interface KnowledgePromptInstance {
  id: string;
  /** Phase 2 Block 2A Cluster B — knowledge__prompt.short_id (kpi-prefix).
   *  Used by list pages to navigate to /prompts/:short_id and /admin/prompts/:short_id. */
  short_id: string;
  /** Null for fully-custom prompts not cloned from a template. */
  template_id: string | null;
  /** Joined from template.name when template_id is non-null. */
  template_slug: string | null;
  name: string;
  display_name: string;
  description: string | null;
  body: string;
  visibility: 'account_installed' | 'account_custom' | 'user_private' | 'user_shared';
  /** Curated bucket — Productivity, Data, Marketing, etc. Drives picker
   *  groupings. Optional for backward compat with older API responses. */
  category?: string | null;
  /** D-151: connector template IDs this prompt is tagged to. Empty array =
   *  universal prompt. Non-empty = surface this prompt to users who have
   *  access to a connector instance pointing at one of these templates.
   *  Optional for backward compat with older API responses. */
  connector_template_ids?: string[];
  /** DB column `account_user_id` — null for account-scoped prompts. */
  created_by_user_id: string | null;
  created_at: string;
}

/* ---------------- Prompt sharing (user-owned prompts) ----------------
 * Per Ean 2026-06-10 sharing-model refactor. A user who authored a prompt
 * can share it with specific teammates post-creation. The sharing path
 * creates knowledge__prompt_assignment rows on behalf of the author —
 * the RLS write carve-out in migration 0040 admits the user-self-owned
 * sharing case alongside the existing admin/owner path.
 */

export interface PromptShare {
  account_user_id: string;
  display_name: string;
  email: string | null;
  granted_at: string;
}

export function fetchPromptShares(promptInstanceId: string): Promise<{ shares: PromptShare[] }> {
  return get(`/api/me/knowledge/instances/${encodeURIComponent(promptInstanceId)}/share`);
}

export function sharePromptWithTeammates(
  promptInstanceId: string,
  accountUserIds: string[],
): Promise<{ ok: true; count: number }> {
  return post(`/api/me/knowledge/instances/${encodeURIComponent(promptInstanceId)}/share`, {
    account_user_ids: accountUserIds,
  });
}

export async function unsharePromptFromTeammates(
  promptInstanceId: string,
  accountUserIds: string[],
): Promise<{ ok: true; count: number }> {
  const res = await fetch(
    `/api/me/knowledge/instances/${encodeURIComponent(promptInstanceId)}/share`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_user_ids: accountUserIds }),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const { message, detail, incidentId, code } = extractError(text, '/api/me/knowledge/instances/share', 'DELETE', res.status);
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  return res.json();
}

export function fetchKnowledgeCatalog(): Promise<{ templates: KnowledgePromptTemplate[] }> {
  // Push 4-1c-c (2026-07-01): knowledge__prompt_template dropped in
  // Migration 0144. Prompts are Group 2 BYO — no template layer, no
  // catalog to fetch. Stub returns empty array; AdminKnowledge.tsx
  // catalog section renders empty state gracefully.
  return Promise.resolve({ templates: [] });
}

export function fetchKnowledgeInstances(): Promise<{ instances: KnowledgePromptInstance[] }> {
  return get('/api/admin/knowledge/instances');
}

export function cloneKnowledgeTemplate(
  _templateId: string,
  _visibility: KnowledgePromptInstance['visibility'] = 'account_installed',
): Promise<{ ok: true; instance_id: string }> {
  // Push 4-1c-c (2026-07-01): knowledge__prompt_template dropped in
  // Migration 0144. No templates to clone. Function retained as a
  // typed no-op so welcome wizard + admin knowledge callers compile;
  // callsite refactor to remove usage entirely lives in a future push.
  return Promise.reject(
    new ApiError(
      501,
      'Prompt templates were removed — all prompts are now BYO. Create your own via the "Create workspace-wide prompt" button.',
      null,
      null,
      'kpt_dropped',
    ),
  );
}

export function createCustomKnowledgePrompt(payload: {
  name: string;
  description?: string;
  body: string;
  visibility: KnowledgePromptInstance['visibility'];
}): Promise<{ ok: true; instance_id: string }> {
  return post('/api/admin/knowledge/instances', payload);
}

/** User-side: which prompts are visible to me (respects RLS Pattern 3 visibility filter). */
export function fetchMyKnowledgePrompts(): Promise<{ prompts: KnowledgePromptInstance[] }> {
  return get('/api/me/knowledge');
}

/** User-side custom prompt creation per Ean 2026-06-09. Users can only
 *  author `user_private` (just me) or `user_shared` (whole workspace)
 *  prompts. Admin-scoped visibilities are rejected at the endpoint. */
export function createMyKnowledgePrompt(payload: {
  name: string;
  description?: string;
  body: string;
  visibility: 'user_private' | 'user_shared';
}): Promise<{ ok: true; instance_id: string }> {
  return post('/api/me/knowledge/instances', payload);
}

export interface MyKnowledgeCatalogRow {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  category: string | null;
  is_installed: boolean;
  is_mine: boolean;
  is_preview: boolean;
}

export function fetchMyKnowledgeCatalog(): Promise<{ templates: MyKnowledgeCatalogRow[] }> {
  // Push 4-1c-c (2026-07-01): same stub as fetchKnowledgeCatalog above.
  // kpt catalog surface is obsolete; user-side pages render own prompts
  // via fetchMyKnowledgePrompts.
  return Promise.resolve({ templates: [] });
}

export interface PromptPermissionSetMembership {
  permission_set_id: string;
  permission_set_name: string;
  permission_set_display_name: string;
  kind: string;
}

export interface MyKnowledgeDetail {
  instance: {
    id: string;
    /** Phase 2 Block 2A — knowledge__prompt.short_id (kpi-prefix). */
    short_id: string;
    template_id: string | null;
    template_slug: string | null;
    template_version_pinned: number | null;
    name: string;
    display_name: string;
    description: string | null;
    body: string;
    variables: Array<{ name: string; description?: string; required?: boolean }> | null;
    category: string | null;
    visibility: 'account_installed' | 'account_custom' | 'user_private' | 'user_shared';
    enabled: boolean;
    created_by_user_id: string | null;
    created_by_display_name: string | null;
    created_at: string;
    updated_at: string;
  };
  permission_sets: PromptPermissionSetMembership[];
  recent_usage: Array<unknown>;
}

/** User-side: full detail record for a single prompt visible to me. */
/**
 * Phase 2 Block 2A Cluster B (2026-06-30) — caller passes
 * knowledge__prompt.short_id (kpi-prefix). Server WHERE i.short_id = $1.
 */
export function fetchMyKnowledgeDetail(short_id: string): Promise<MyKnowledgeDetail> {
  return get(`/api/me/knowledge/${encodeURIComponent(short_id)}`);
}
